import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'longform', label: '롱폼' },
  { key: 'shortform', label: '숏폼' },
];

export default function VideoLibrary() {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadVideos = useCallback(async () => {
    try {
      const r = await api.get('/video/library');
      setVideos(r.data.videos || []);
    } catch (e) {
      setError(e.response?.data?.error || '영상 목록 로드 실패');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  const handleDelete = async (id) => {
    if (!window.confirm('이 영상을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/video/${id}`);
      setVideos(prev => prev.filter(v => v.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || '삭제 실패');
    }
  };

  const filtered = filter === 'all' ? videos : videos.filter(v => v.type === filter);

  const formatDuration = (sec) => {
    if (!sec) return '--:--';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes > 1024 * 1024 * 1024) return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <div>
      <div style={S.headerRow}>
        <div>
          <h1 style={S.h1}>영상 보관함</h1>
          <p style={S.desc}>완성된 영상을 관리하고 다운로드합니다</p>
        </div>
        <button style={S.primaryBtn} onClick={() => navigate('/video/editor')}>
          + 새 영상 편집
        </button>
      </div>

      {error && (
        <div style={S.errorBox}>
          {error}
          <button style={S.errorClose} onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* 필터 탭 */}
      <div style={S.filterBar}>
        {FILTERS.map(f => (
          <button key={f.key}
            onClick={() => setFilter(f.key)}
            style={{ ...S.filterBtn, ...(filter === f.key ? S.filterBtnActive : {}) }}>
            {f.label}
            {f.key === 'all' ? ` (${videos.length})` :
              ` (${videos.filter(v => v.type === f.key).length})`}
          </button>
        ))}
      </div>

      {/* 영상 그리드 */}
      {loading ? (
        <div style={S.center}>
          <div style={S.spinner} />
          <p style={{ color: '#8B949E', marginTop: 16 }}>영상 목록 로딩 중...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={S.center}>
          <span style={{ fontSize: 48, opacity: 0.5 }}>📁</span>
          <p style={{ color: '#8B949E', marginTop: 12 }}>
            {filter === 'all' ? '보관된 영상이 없습니다' : `${filter === 'longform' ? '롱폼' : '숏폼'} 영상이 없습니다`}
          </p>
          <button style={S.ghostBtn} onClick={() => navigate('/video/editor')}>
            영상 편집하러 가기
          </button>
        </div>
      ) : (
        <div style={S.grid}>
          {filtered.map(v => (
            <div key={v.id} style={S.card}>
              {/* 썸네일 */}
              <div style={S.thumbWrap}>
                <div style={S.thumbPlaceholder}>🎬</div>
                {/* 타입 뱃지 */}
                <div style={{
                  ...S.typeBadge,
                  backgroundColor: v.type === 'shortform' ? '#7C3AED' : '#2E7D32',
                }}>
                  {v.type === 'shortform' ? '숏폼' : '롱폼'}
                </div>
                {/* 재생 시간 */}
                {v.duration && (
                  <div style={S.durationBadge}>{formatDuration(v.duration)}</div>
                )}
              </div>

              {/* 정보 */}
              <div style={S.cardBody}>
                <p style={S.cardTitle} title={v.filename}>{v.filename}</p>
                <div style={S.cardMeta}>
                  <span>{formatDate(v.createdAt)}</span>
                  {v.size && <span>{formatSize(v.size)}</span>}
                </div>
              </div>

              {/* 액션 버튼 */}
              <div style={S.cardActions}>
                <a href={v.downloadUrl} download style={S.actionBtn} title="다운로드">
                  다운로드
                </a>
                <button style={S.actionBtn}
                  onClick={() => navigate(`/video/editor/edit?ids=${v.id}`)}
                  title="편집">
                  편집
                </button>
                <button style={{ ...S.actionBtn, ...S.actionBtnDanger }}
                  onClick={() => handleDelete(v.id)}
                  title="삭제">
                  삭제
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const S = {
  h1: { color: '#E6EDF3', fontSize: 24, fontWeight: 700, margin: 0 },
  desc: { color: '#8B949E', fontSize: 14, margin: '4px 0 0' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },

  errorBox: { backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444', borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  errorClose: { background: 'none', border: 'none', color: '#EF4444', fontSize: 16, cursor: 'pointer' },

  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' },

  /* 필터 */
  filterBar: { display: 'flex', gap: 8, marginBottom: 24 },
  filterBtn: { padding: '10px 20px', backgroundColor: '#1E2732', border: '1px solid #2A3441', borderRadius: 10, color: '#8B949E', fontSize: 14, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif", fontWeight: 500 },
  filterBtnActive: { borderColor: '#4CAF50', color: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)' },

  /* 그리드 3열 */
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },

  /* 카드 */
  card: { backgroundColor: '#1E2732', borderRadius: 16, border: '1px solid #2A3441', overflow: 'hidden' },
  thumbWrap: { width: '100%', height: 180, backgroundColor: '#0D1117', position: 'relative' },
  thumbPlaceholder: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, opacity: 0.3 },
  typeBadge: { position: 'absolute', top: 10, left: 10, padding: '3px 10px', borderRadius: 6, color: '#fff', fontSize: 11, fontWeight: 700 },
  durationBadge: { position: 'absolute', bottom: 10, right: 10, backgroundColor: 'rgba(0,0,0,0.75)', color: '#fff', padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600 },
  cardBody: { padding: '14px 16px 10px' },
  cardTitle: { color: '#E6EDF3', fontSize: 14, fontWeight: 600, margin: '0 0 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta: { display: 'flex', gap: 12, color: '#8B949E', fontSize: 12 },
  cardActions: { display: 'flex', gap: 8, padding: '0 16px 14px', borderTop: '1px solid #2A3441', paddingTop: 12 },
  actionBtn: { flex: 1, padding: '8px 0', backgroundColor: '#161B22', border: '1px solid #2A3441', borderRadius: 8, color: '#C9D1D9', fontSize: 12, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif", textAlign: 'center', textDecoration: 'none', display: 'inline-block' },
  actionBtnDanger: { color: '#EF4444', borderColor: 'rgba(239,68,68,0.3)' },

  /* 버튼 */
  primaryBtn: { padding: '12px 24px', backgroundColor: '#2E7D32', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },
  ghostBtn: { padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #2A3441', borderRadius: 10, color: '#8B949E', fontSize: 13, cursor: 'pointer', fontFamily: "'Noto Sans KR',sans-serif" },

  spinner: { width: 48, height: 48, border: '4px solid #2A3441', borderTop: '4px solid #4CAF50', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
